/**
 * Currently only used for editing.
 * This should be merged with the version in track-time/index.tsx
 */

import Icon from '../../atoms/icon';
import { Classes } from '@blueprintjs/core';
import { faStopwatch, faWindowClose } from '@fortawesome/pro-solid-svg-icons';
import { Col, Row } from '../../../grid';
import Button from '../../molecules/button';
import { Form, FormInstance } from 'blueprint-form';
import SelectPatientDrawer from '../../organisms/patient/search';
import { CardBlock } from '../../organisms/card';
import Dialog from '../../organisms/dialog';
import Modal from '../../organisms/modal';
import {
  TrackTimePatientCard,
  TrackTimePatientPlaceholderCard
} from '../../organisms/track-time';
import React, { useState } from 'react';
import { useTerms } from '../../../contexts/terms';
import { BaseResponse, PostJSON } from '../../../libs/request';
import { ITalk } from './types';

interface TimeTrackedFormProps {
  close: () => void;
  talk: ITalk;
}

const TimeTrackedForm = ({
  close,
  talk
}: TimeTrackedFormProps): JSX.Element => {
  const { terms } = useTerms();
  const form = new FormInstance({ note: '' });
  const [isPatientDrawerOpen, setIsPatientDrawerOpen] = useState(false);

  const openPatientDrawer = () => {
    setIsPatientDrawerOpen(true);
  };
  const [patient, setPatient] = useState(talk.patient);
  const [note, setNote] = useState(talk.note);
  const [minutes, setMinutes] = useState(Math.round(talk.duration / 60));
  const [seconds, setSeconds] = useState(Math.round(talk.duration % 60));
  const [isSubmitting, setIsSubmitting] = useState(false);

  const updateEntry = async () => {
    setIsSubmitting(true);
    const response = await PostJSON<BaseResponse>({
      url: '/talk/edit/',
      params: {
        duration: minutes * 60 + seconds,
        initiatedByProvider: true,
        patient: patient.id,
        note,
        id: talk.id
      }
    });
    if (response.status === 'OK') {
      Modal.success({
        title: 'Entry Updated',
        content: 'The entry has been successfully updated!'
      });
      close();
    } else {
      Modal.error({
        title: 'Error',
        content:
          response.message ||
          'We were unable to add your time entry at this time.',
        portalClassName: 'z-201'
      });
    }
    setIsSubmitting(false);
  };

  return (
    <Dialog className="fixed z-200" isOpen={true} onClose={close} forceScrollY>
      <div
        className={Classes.DIALOG_HEADER}
        style={{ justifyContent: 'space-between' }}
      >
        <h3>
          <Icon icon={faStopwatch} className="text-black text-sans-lg mr-2" />
          Track Time Spent on {terms.patient.capitalized}
        </h3>
        <Button
          style={{ border: '0', boxShadow: 'none', background: 'none' }}
          onClick={close}
        >
          <Icon icon={faWindowClose} className="text-black" />
        </Button>
      </div>
      <div className={Classes.DIALOG_BODY}>
        <Form form={form} onSubmit={updateEntry}>
          <CardBlock>
            <Row>
              <Col sm={6}>
                <span
                  style={{
                    fontWeight: 600,
                    display: 'inline-block'
                  }}
                  className="bp5-label mb-2"
                >
                  {terms.patient.capitalized}
                </span>
              </Col>
            </Row>
            <Row
              style={{
                alignItems: 'center',
                marginBottom: '30px',
                justifyContent: 'space-between'
              }}
            >
              <Col>
                {patient ? (
                  <TrackTimePatientCard patient={patient} close={close} />
                ) : (
                  <TrackTimePatientPlaceholderCard />
                )}
              </Col>
              <Col style={{ textAlign: 'right', padding: '0px' }}>
                <Button id="timeTrackPatient" onClick={openPatientDrawer}>
                  {patient ? 'Change' : 'Choose'} {terms.patient.capitalized}
                </Button>
              </Col>
            </Row>
            <Row>
              <Col sm={6}>
                <span
                  style={{
                    fontWeight: 600,
                    display: 'inline-block'
                  }}
                  className="bp5-label mb-2"
                >
                  Time Elapsed
                </span>
              </Col>
            </Row>
            <Row style={{ marginBottom: '30px' }}>
              <Col>
                <input
                  style={{ width: '50px' }}
                  name="timeElapsedMin"
                  className="bp5-input text-right"
                  placeholder={`${minutes}`}
                  value={minutes}
                  min="0"
                  onChange={({ target: { value } }) =>
                    setMinutes(parseInt(value.replaceAll('.', '')) || 0)
                  }
                  onBlur={({ target: { value } }) =>
                    setMinutes(parseInt(value.replaceAll('.', '')) || 0)
                  }
                  type="number"
                />{' '}
                min :
                <input
                  name="timeElapsedSec"
                  className="bp5-input text-right"
                  placeholder={`${seconds}`}
                  value={seconds}
                  min="0"
                  max="59"
                  onChange={({ target: { value } }) =>
                    setSeconds(parseInt(value.replaceAll('.', '')) || 0)
                  }
                  onBlur={({ target: { value } }) =>
                    setSeconds(parseInt(value.replaceAll('.', '')) || 0)
                  }
                  type="number"
                />{' '}
                sec
              </Col>
            </Row>
            <Row>
              <Col sm={6}>
                <span
                  style={{
                    fontWeight: 600,
                    display: 'inline-block'
                  }}
                  className="bp5-label mb-2"
                >
                  Note (optional)
                </span>
              </Col>
            </Row>
            <Row>
              <Col>
                <textarea
                  name="note"
                  className="bp5-input w-full"
                  style={{ height: '100px' }}
                  onChange={({ target: { value } }) => setNote(value)}
                  onBlur={({ target: { value } }) => setNote(value)}
                  value={note}
                />
              </Col>
            </Row>
          </CardBlock>

          <div className={Classes.DIALOG_FOOTER} style={{ textAlign: 'right' }}>
            <Button
              intent="success"
              type="submit"
              disabled={(minutes === 0 && seconds === 0) || isSubmitting}
            >
              Update Entry
            </Button>
          </div>
        </Form>
      </div>
      {isPatientDrawerOpen ? (
        <SelectPatientDrawer
          isOpen={isPatientDrawerOpen}
          setIsOpen={(isOpen) => setIsPatientDrawerOpen(isOpen)}
          setPatient={setPatient}
        />
      ) : null}
    </Dialog>
  );
};

export default TimeTrackedForm;
